import { ChangeDetectorRef, Component, inject, output, signal } from '@angular/core';
import { Loading01 } from '../../../infrastructure/shared/icons/loading-01/loading-01';
import { ClockRewind } from '../../../infrastructure/shared/icons/clock-rewind/clock-rewind';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import {
  GetPackagesForAdminQuery,
  IPackageFilter,
  IPaginationResponseOfPackageForAdminTableResponse,
  PackageForAdminTableResponse,
  PackagesClient,
  SetUserPackageStatusCommand,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { debounceTime, firstValueFrom, Subscription } from 'rxjs';
import { MIN_LOADING_TIME_MS } from '../../../infrastructure/shared/constants';
import { Skeleton } from 'primeng/skeleton';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { Chip } from 'primeng/chip';
import { DataTable } from '../../../infrastructure/shared/components/data-table/data-table';
import { getDateFormatedWithTime } from '../../../infrastructure/shared/services/date.service';
import { Send01 } from '../../../infrastructure/shared/icons/send-01/send-01';
import { CheckCircle } from '../../../infrastructure/shared/icons/check-circle/check-circle';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';
import { ClientTypeDropdown } from '../../../infrastructure/shared/components/client-type-dropdown/client-type-dropdown';
import { PackageStatusDropdown } from '../../../infrastructure/shared/components/package-status-dropdown/package-status-dropdown';
import { TextInput } from '../../../infrastructure/shared/ui/text-input/text-input';
import { FormControlPipe } from '../../../infrastructure/shared/pipes/form-control.pipe';
import { Search } from '../../../infrastructure/shared/icons/search/search';
import { FormControl } from '@angular/forms';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { InvoiceSentDialog } from './invoice-sent-dialog/invoice-sent-dialog';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import { Badge } from 'primeng/badge';
import { ConfirmPaymentDialog } from '../../../infrastructure/shared/components/confirm-payment-dialog/confirm-payment-dialog';
import { ConfirmActionDialog } from '../../../infrastructure/shared/components/confirm-action-dialog/confirm-action-dialog';

@Component({
  standalone: true,
  selector: 'app-admin-packages-table',
  imports: [
    Tab,
    TabList,
    Tabs,
    Loading01,
    ClockRewind,
    Skeleton,
    Building05,
    UserCircle,
    ButtonComponent,
    Chip,
    Send01,
    CheckCircle,
    SlashCircle01,
    ClientTypeDropdown,
    PackageStatusDropdown,
    TextInput,
    FormControlPipe,
    Search,
    InvoiceSentDialog,
    Badge,
    DataTable,
    ConfirmPaymentDialog,
    ConfirmActionDialog,
  ],
  templateUrl: './admin-packages-table.html',
  styleUrl: './admin-packages-table.scss',
})
export class AdminPackagesTable {
  private _subs = new Subscription();
  private _client = inject(PackagesClient);
  private _cdr = inject(ChangeDetectorRef);
  private _loader = inject(LoaderService);
  private _alert = inject(AlertService);
  private _location = inject(LocationAdminService);

  packages: PackageForAdminTableResponse[] = [];
  first = 0;
  isLoading = true;
  filter: IPackageFilter | undefined;
  hasActiveFilters: boolean = false;
  activeTab: number = 0;
  resetCounter: number = 0;
  searchFormCtrl: FormControl = new FormControl(undefined);

  paginatedData: IPaginationResponseOfPackageForAdminTableResponse = {
    currentPage: 1,
    pageSize: 4,
    totalCount: 0,
  } as IPaginationResponseOfPackageForAdminTableResponse;

  /** Count of in-process requests shown as a badge on the "U procesu" tab */
  inProcessCount: number | undefined;

  onAction = output<void>();

  /** Invoice dialog */
  showInvoiceDialog = signal(false);
  selectedCommand = signal<SetUserPackageStatusCommand | undefined>(undefined);

  /** Confirm payment dialog */
  showConfirmPaymentDialog = signal(false);
  selectedId = signal<string | undefined>(undefined);

  /** Cancel (reject) request confirmation dialog */
  showCancelDialog = signal(false);
  cancelId = signal<string | undefined>(undefined);

  constructor() {
    this.filter = {
      statuses: [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT],
      userId: undefined,
      keyword: undefined,
      isCompany: undefined,
    } as IPackageFilter;

    this._subs.add(
      this.searchFormCtrl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
        if (value === '') {
          value = undefined;
        }
        this.filter!.keyword = value;

        this.paginatedData.currentPage = 1;
        this.load();
      }),
    );

    this._subs.add(
      this._alert.requestCountChanged$.subscribe(() => this.load()),
    );
  }

  loadMeetingsLazy(event: any) {
    this.paginatedData.pageSize = event.rows;
    this.paginatedData.currentPage = Math.floor(event.first / event.rows) + 1;
    this.load();
  }

  next() {
    this.paginatedData!.currentPage!++;
    this.load();
  }

  prev() {
    this.paginatedData!.currentPage!--;
    this.load();
  }

  private load() {
    this.isLoading = true;
    this.packages = [];
    this._checkHasActiveFilters();
    this._cdr.detectChanges();
    const start = Date.now();

    this._loadMeetings().then((res) => {
      const elapsed = Date.now() - start;
      const delay = Math.max(0, MIN_LOADING_TIME_MS - elapsed);

      setTimeout(() => {
        this.packages = res.result.data ?? [];
        this.paginatedData = res.result;
        this.inProcessCount = res.result.additionalData;
        this.first = (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
        this.isLoading = false;
        this._cdr.markForCheck();
      }, delay);
    });
  }

  private async _loadMeetings() {
    return await firstValueFrom(
      this._client.getPackagesForAdmin({
        filter: this.filter,
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetPackagesForAdminQuery),
    );
  }

  onTabChange(value: any) {
    this.activeTab = value;
    if (this.activeTab === 0) {
      this.filter!.statuses = [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT];
    } else {
      this.filter!.statuses = [
        UserPackageStatus.ACTIVE,
        UserPackageStatus.EXTENDED,
        UserPackageStatus.CANCELLED,
        UserPackageStatus.EXPIRED,
      ];
    }

    this.paginatedData.currentPage = 1;
    this.load();
  }

  onClientTypeChange(value: boolean | undefined) {
    this.filter!.isCompany = value;

    this.paginatedData.currentPage = 1;
    this.load();
  }

  onStatusChange(value: UserPackageStatus | undefined) {
    this.paginatedData.currentPage = 1;
    if (value !== undefined) {
      this.filter!.statuses = [value];
    } else {
      this.onTabChange(this.activeTab);
    }

    this.load();
  }

  resetFilters() {
    this.onTabChange(this.activeTab);
    this.filter!.isCompany = undefined;
    this.searchFormCtrl.patchValue(undefined);
    this.resetCounter++;
  }

  private _checkHasActiveFilters() {
    this.hasActiveFilters =
      this.filter?.keyword !== undefined ||
      this.filter?.statuses?.length === 1 ||
      this.filter?.isCompany !== undefined;
  }

  getStatusChipClass(status: UserPackageStatus): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'info sm';
      case UserPackageStatus.INVOICE_SENT:
        return 'warn sm';
      case UserPackageStatus.ACTIVE:
      case UserPackageStatus.EXTENDED:
        return 'success sm';
      case UserPackageStatus.EXPIRED:
        return 'gray sm';
      default:
        return 'red sm';
    }
  }

  openCancelDialog(id: string) {
    this.cancelId.set(id);
    this.showCancelDialog.set(true);
  }

  closeCancelDialog() {
    this.showCancelDialog.set(false);
    this.cancelId.set(undefined);
  }

  confirmCancel() {
    const id = this.cancelId();
    this.closeCancelDialog();
    if (id) this.cancel(id);
  }

  cancel(id: string) {
    let req = {
      id: id,
      status: UserPackageStatus.CANCELLED,
    } as SetUserPackageStatusCommand;

    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this.onAction.emit();
      this.load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  setAsPaid() {
    let req = {
      id: this.selectedId()!,
      status: UserPackageStatus.ACTIVE,
    } as SetUserPackageStatusCommand;

    this._loader.setLoadingText = 'Čuvanje u toku...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this.selectedId.set(undefined);
      this.showConfirmPaymentDialog.set(false);
      this.onAction.emit();
      this.load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  openInvoicePaidDialog(id: string) {
    this.selectedId.set(id);
    this.showConfirmPaymentDialog.set(true);
  }

  setInvoiceSent(req: SetUserPackageStatusCommand) {
    this._loader.setLoadingText = 'Slanje fakture...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this.showInvoiceDialog.set(false);
      this.onAction.emit();
      this.load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  openInvoiceDialog(id: string, price: number) {
    let req = {
      id: id,
      status: UserPackageStatus.INVOICE_SENT,
      packagePrice: {
        price: price,
        originalPrice: price,
      },
    } as SetUserPackageStatusCommand;

    this.selectedCommand.set(req);
    this.showInvoiceDialog.set(true);
  }

  routeToPackageDetails(id: string) {
    this._location.routeToPackageDetails(id);
  }

  private async _setStatus(req: SetUserPackageStatusCommand) {
    return await firstValueFrom(this._client.setStatus(req));
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly getDateFormatedWithTime = getDateFormatedWithTime;
  protected readonly UserPackageStatus = UserPackageStatus;
}
